import gsap from "gsap";

export let cursor: {
    addState(state: string): void;
    removeState(state: string): void;
    setText(text: string): void;
};

const speed = 1;
const trailCount = 2;
const trailDelay = 70;
const cursorSizeStates = ["-link", "-minor-link", "-medium-link", "-major-link", "-video", "-close", "-drag"] as const;

let cursorEl: HTMLElement | null = null;
let textEl: HTMLElement | null = null;
let linkHoverActive = false;
let currentCursorText = "";
let activeDataCursorState = "";
let mouseX = 0;
let mouseY = 0;

interface PosRecord {
    x: number;
    y: number;
    t: number;
}

function rawAddState(state: string): void {
    if (!cursorEl) return;

    const tokens = state.split(/\s+/).filter(Boolean);
    if (tokens.length) cursorEl.classList.add(...tokens);
}

function rawRemoveState(state: string): void {
    if (!cursorEl) return;

    const tokens = state.split(/\s+/).filter(Boolean);
    if (tokens.length) cursorEl.classList.remove(...tokens);
}

function rawSetText(text: string): void {
    if (!textEl || !cursorEl) return;

    textEl.textContent = text;
    cursorEl.classList.toggle("-text", text.length > 0);
}

function toStateTokens(state: string): string[] {
    return state
        .split(/\s+/)
        .map(token => token.trim())
        .filter(Boolean);
}

function isSizeState(state: string): boolean {
    return (cursorSizeStates as readonly string[]).includes(state);
}

function applyDataCursorState(state: string): void {
    const tokens = toStateTokens(state);
    const sizeTokens = tokens.filter(isSizeState);
    const requestedSizeState = sizeTokens.length ? sizeTokens[sizeTokens.length - 1] : "";
    const previousTokens = toStateTokens(activeDataCursorState);

    previousTokens.forEach(token => {
        if (!tokens.includes(token)) rawRemoveState(token);
    });

    cursorSizeStates.forEach(s => {
        if (s !== requestedSizeState) rawRemoveState(s);
    });

    const nonSizeTokens = tokens.filter(token => !isSizeState(token));
    if (nonSizeTokens.length) rawAddState(nonSizeTokens.join(" "));
    if (requestedSizeState) rawAddState(requestedSizeState);

    activeDataCursorState = state;
}

function suppressTextZone(): void {
    if (!currentCursorText) return;
    rawSetText("");
}

function restoreTextZone(): void {
    if (!currentCursorText) return;
    rawSetText(currentCursorText);
}

export function resetCursorState() {
    if (!cursorEl || !textEl) return;

    toStateTokens(activeDataCursorState).forEach(token => rawRemoveState(token));
    cursorSizeStates.forEach(s => rawRemoveState(s));
    hoverElements.forEach(({ cursorClass }) => rawRemoveState(cursorClass));
    rawRemoveState("-active");

    activeDataCursorState = "";
    currentCursorText = "";
    linkHoverActive = false;
    rawSetText("");
}

export function recomputeCursorState() {
    if (!cursorEl) return;
    const target = document.elementFromPoint(mouseX, mouseY);
    if (!target || !(target instanceof Element)) return;

    for (const { selector, cursorClass } of hoverElements) {
        const matchedEl = target.closest<HTMLElement>(selector);
        if (!matchedEl) continue;

        cursor.addState(cursorClass);
        if (isSizeState(cursorClass)) {
            linkHoverActive = true;

            suppressTextZone();

            if (matchedEl.hasAttribute("data-cursor")) {
                applyDataCursorState(matchedEl.getAttribute("data-cursor") ?? "");
            }
        }
    }

    if (!linkHoverActive && !isHoverElementTarget(target)) {
        const dataCursorEl = getDataCursorElement(target);
        applyDataCursorState(dataCursorEl?.getAttribute("data-cursor") ?? "");
    }

    setCursorTextFromElement(getTextTriggerElement(target));
}

export default function initCursor() {
    const el = document.createElement("div");
    el.className = "mf-cursor -hidden";

    const txt = document.createElement("div");
    txt.className = "mf-cursor-text";
    el.appendChild(txt);

    document.body.appendChild(el);
    cursorEl = el;
    textEl = txt;

    cursor = {
        addState(state: string) {
            const tokens = toStateTokens(state);
            const requestedSizeStates = tokens.filter(isSizeState);

            if (requestedSizeStates.length) {
                if (linkHoverActive) {
                    const nonSizeTokens = tokens.filter(t => !isSizeState(t));
                    if (nonSizeTokens.length) rawAddState(nonSizeTokens.join(" "));
                    return;
                }

                const winner = requestedSizeStates[requestedSizeStates.length - 1];
                cursorSizeStates.forEach(s => {
                    if (s !== winner) rawRemoveState(s);
                });
            }

            rawAddState(state);
        },

        removeState(state: string) {
            rawRemoveState(state);
        },

        setText(text: string) {
            const nextText = typeof text === "string" ? text : "";
            currentCursorText = nextText;

            if (!linkHoverActive) {
                rawSetText(nextText);
            }
        },
    };

    let curX = 0;
    let curY = 0;
    let started = false;

    document.addEventListener("mousemove", (e: MouseEvent) => {
        mouseX = e.clientX;
        mouseY = e.clientY;

        if (!started) {
            started = true;

            curX = mouseX;
            curY = mouseY;

            gsap.set(el, { x: curX, y: curY });
            el.classList.remove("-hidden");
        }
    });

    document.addEventListener("mouseleave", () => el.classList.add("-hidden"));
    document.addEventListener("mouseenter", () => el.classList.remove("-hidden"));
    document.addEventListener("mousedown", () => rawAddState("-active"));
    document.addEventListener("mouseup", () => rawRemoveState("-active"));

    gsap.ticker.add(() => {
        if (!started) return;

        curX += (mouseX - curX) * speed;
        curY += (mouseY - curY) * speed;

        gsap.set(el, { x: curX, y: curY });
    });

    initHovers();
    initCursorTextHandoff();
    initDataCursorZoneHandoff();
    initTrail();
}

const hoverElements = [
    { selector: "a, button, .-hover-link, .track, .filter-group", cursorClass: "-minor-link" },
    { selector: "img, video, .header--white, .track, .-hover-white", cursorClass: "-white" },
];

function getDataCursorElement(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>("[data-cursor]");
}

function isHoverElementTarget(target: Element): boolean {
    return hoverElements.filter(({ cursorClass }) => isSizeState(cursorClass)).some(({ selector }) => target.closest(selector) !== null);
}

function initHovers() {
    document.addEventListener("mouseover", event => {
        const target = event.target as Element;
        const from = (event as MouseEvent).relatedTarget as Element | null;

        for (const { selector, cursorClass } of hoverElements) {
            const matchedEl = target.closest<HTMLElement>(selector);
            if (!matchedEl || matchedEl.contains(from)) continue;

            cursor.addState(cursorClass);
            if (isSizeState(cursorClass)) {
                linkHoverActive = true;
                suppressTextZone();
            }

            if (isSizeState(cursorClass) && matchedEl.hasAttribute("data-cursor")) {
                applyDataCursorState(matchedEl.getAttribute("data-cursor") ?? "");
            }
        }
    });

    document.addEventListener("mouseout", event => {
        const target = event.target as Element;
        const to = (event as MouseEvent).relatedTarget as Element | null;

        for (const { selector, cursorClass } of hoverElements) {
            const matchedEl = target.closest<HTMLElement>(selector);
            if (!matchedEl || matchedEl.contains(to)) continue;

            cursor.removeState(cursorClass);
            if (isSizeState(cursorClass)) {
                linkHoverActive = false;

                const dest = getDataCursorElement(to);
                applyDataCursorState(dest?.getAttribute("data-cursor") ?? "");
                restoreTextZone();
            }
        }
    });
}

function initDataCursorZoneHandoff() {
    document.addEventListener("mouseover", event => {
        if (linkHoverActive) return;
        if (isHoverElementTarget(event.target as Element)) return;

        const targetEl = getDataCursorElement(event.target);
        applyDataCursorState(targetEl?.getAttribute("data-cursor") ?? "");
    });

    document.addEventListener("mouseout", event => {
        if (linkHoverActive) return;

        const fromEl = getDataCursorElement(event.target);
        if (!fromEl) return;

        const toEl = getDataCursorElement((event as MouseEvent).relatedTarget);
        if (toEl === fromEl) return;

        applyDataCursorState(toEl?.getAttribute("data-cursor") ?? "");
    });
}

function getTextTriggerElement(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>("[data-cursor-text]");
}

function setCursorTextFromElement(el: HTMLElement | null): void {
    const previousText = currentCursorText;
    const nextText = el?.getAttribute("data-cursor-text") ?? "";
    if (nextText === previousText) return;

    cursor.setText(nextText);
    currentCursorText = nextText;
}

function initCursorTextHandoff() {
    document.addEventListener("mouseover", event => {
        const targetEl = getTextTriggerElement(event.target);
        if (!targetEl) return;

        setCursorTextFromElement(targetEl);
    });

    document.addEventListener("mouseout", event => {
        const fromEl = getTextTriggerElement(event.target);
        if (!fromEl) return;

        const toEl = getTextTriggerElement((event as MouseEvent).relatedTarget);
        if (toEl === fromEl) return;

        setCursorTextFromElement(toEl);
    });
}

const trailEls: HTMLElement[] = [];
const posHistory: PosRecord[] = [];

function getHistoryPosition(targetTime: number): { x: number; y: number } | null {
    if (!posHistory.length) return null;

    const last = posHistory[posHistory.length - 1];
    if (targetTime >= last.t) return { x: last.x, y: last.y };

    const first = posHistory[0];
    if (targetTime <= first.t) return { x: first.x, y: first.y };

    let lo = 0,
        hi = posHistory.length - 1;

    while (lo < hi - 1) {
        const mid = (lo + hi) >>> 1;
        if (posHistory[mid].t <= targetTime) lo = mid;
        else hi = mid;
    }

    const a = posHistory[lo];
    const b = posHistory[hi];
    const frac = (targetTime - a.t) / (b.t - a.t);
    return {
        x: a.x + (b.x - a.x) * frac,
        y: a.y + (b.y - a.y) * frac,
    };
}

function initTrail() {
    for (let i = 0; i < trailCount; i++) {
        const el = document.createElement("div");
        el.className = "cursor-trail";
        document.body.appendChild(el);

        gsap.set(el, { xPercent: -50, yPercent: -50, rotation: 45, scale: 1.6 });
        trailEls.push(el);
    }

    document.addEventListener("mousemove", (e: MouseEvent) => {
        const now = Date.now();
        posHistory.push({ x: e.clientX, y: e.clientY, t: now });

        const cutoff = now - trailDelay * trailCount * 4;
        while (posHistory.length > 1 && posHistory[0].t < cutoff) posHistory.shift();
    });

    if (cursorEl) {
        const el = cursorEl;

        new MutationObserver(() => {
            const isLink = el.classList.contains("-link");
            const isWhite = el.classList.contains("-white");

            trailEls.forEach((trailEl, i) => {
                trailEl.classList.toggle("-white", isWhite);
                trailEl.style.opacity = isLink ? String((1 - i / trailCount) * 0.8) : "0";
            });
        }).observe(el, { attributes: true, attributeFilter: ["class"] });
    }

    gsap.ticker.add(() => {
        const now = Date.now();

        trailEls.forEach((el, i) => {
            const pos = getHistoryPosition(now - (i + 1) * trailDelay);
            if (pos) gsap.set(el, { x: pos.x, y: pos.y });
        });
    });
}
